/**
 * [Phase 2c.6-A.2.5] Auction-side voice variety helpers. Mirrors
 * `snakeTemplates.js` discipline - pulls phrase pools from the shared
 * `voice_pools.json` and substitutes placeholders. Each helper picks a
 * variant uniformly at random (or via the optional `rng` for tests).
 *
 * Pools are large (~20 each) so users hear meaningful variety across the
 * 700+ auction events of a typical draft. Curly v3 tone discipline applies
 * - phrases must read like a quick auctioneer call, not a sportscaster
 * recap.
 *
 * Public API:
 *   auctionNominationLine({team, player, flavor, teamFrag, bid}, rng?) → string
 *   auctionBidAcceptedLine({team, amount}, rng?) → string
 *   auctionGoingOnceLine({player, bid}, rng?) → string
 *   auctionGoingTwiceLine({player}, rng?) → string
 *
 * `teamFrag` is the pre-rendered " of <team phrase>" fragment from
 * nflTeamPhrase(withArticle:true) - empty string when no NFL team is known.
 * Callers compute it once and pass through (mirrors HF4-3 contract).
 */
import POOLS from "../shared/voice_pools.json";

const _NOMINATION = POOLS.auction_nomination_phrases;
const _BID_ACCEPTED = POOLS.auction_bid_accepted_phrases;
const _GOING_ONCE = POOLS.auction_going_once_phrases;
const _GOING_TWICE = POOLS.auction_going_twice_phrases;

// Exported for parity + variety unit tests.
export const AUCTION_NOMINATION_PHRASES = _NOMINATION;
export const AUCTION_BID_ACCEPTED_PHRASES = _BID_ACCEPTED;
export const AUCTION_GOING_ONCE_PHRASES = _GOING_ONCE;
export const AUCTION_GOING_TWICE_PHRASES = _GOING_TWICE;

function _pick(arr, rng) {
  const r = typeof rng === "function" ? rng : Math.random;
  return arr[Math.floor(r() * arr.length)];
}

/** Replace every {key} occurrence with vars[key]. Missing keys → empty string. */
function _interpolate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) && vars[k] != null
      ? String(vars[k])
      : "",
  );
}

/**
 * Auction nomination opener. Combines team, player, position-flavor,
 * NFL team fragment, and opening bid into a Curly-tone line.
 *
 * @param {object} v
 * @param {string} v.team       e.g. "Stat Stalkers"
 * @param {string} v.player     e.g. "Jaylen Waddle"
 * @param {string} v.flavor     e.g. "pass-catcher"
 * @param {string} v.teamFrag   e.g. " of the Dolphins" (already prefixed " of " or empty)
 * @param {number|string} v.bid e.g. 1
 */
export function auctionNominationLine(v, rng) {
  const template = _pick(_NOMINATION, rng);
  return _interpolate(template, {
    team: v?.team ?? "",
    player: v?.player ?? "",
    flavor: v?.flavor ?? "player",
    teamFrag: v?.teamFrag ?? "",
    bid: v?.bid ?? "",
  });
}

/**
 * Bid-accepted call. Used for both new highest bid and re-emphasis after
 * silence (DraftContext.js BID_ACCEPTED case).
 */
export function auctionBidAcceptedLine(v, rng) {
  const template = _pick(_BID_ACCEPTED, rng);
  return _interpolate(template, {
    team: v?.team ?? "A team",
    amount: v?.amount ?? "0",
  });
}

/** Going-once auctioneer call. */
export function auctionGoingOnceLine(v, rng) {
  const template = _pick(_GOING_ONCE, rng);
  return _interpolate(template, {
    player: v?.player ?? "the pick",
    bid: v?.bid ?? "",
  });
}

/** Going-twice auctioneer call. Final-chance Curly tone. */
export function auctionGoingTwiceLine(v, rng) {
  const template = _pick(_GOING_TWICE, rng);
  return _interpolate(template, {
    player: v?.player ?? "the pick",
  });
}
